import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import authStorage from "../../utils/authStorage";
import MyPerformance from './myPerformance';
import { FEATURE, loadFeatureFlags, isFeatureEnabled } from '../../utils/featureFlags';
import { BASE_URL } from "../../config";

/**
 * Leaderboard — members ranked by progress against their own monthly goal.
 *
 * The goal maths is NOT repeated here. The API computes it with
 * helpers/goalMetrics.js, a port of helper/summaryGoalsReport.js, so this page
 * and a member's own Summary & Goals page can never disagree. This component
 * only renders what the API ranked.
 *
 * There is deliberately no subscription gate: the Leaderboard is available
 * irrespective of membership type. The old placeholder redirected unsubscribed
 * members to /membership, which contradicted that.
 */

// The board shows the top 20 only — no pager. A member ranked below 20 is still
// shown, pinned under the table by the `me` row, which is the reason the API
// returns `me` separately from `rows`: without it, dropping the pager would make
// your own position unreachable.
const TOP_N = 20;

// Admins get a paged, searchable view of everyone; members get the top 20 only.
// Both fetch ONE page at a time from the API -- the board is never loaded whole
// into the browser, and each pager click is a fresh request.
const ADMIN_PAGE_SIZE = 20;

const SCOPE_GLOBAL = 'global';
const SCOPE_ORG = 'organization';
// Not a board scope - a different view entirely, which is why everything below
// short-circuits on it rather than treating it as a third ranking.
const SCOPE_PERFORMANCE = 'performance';

// The month picker is limited to the last three months INCLUDING the current one,
// so monthsBack is clamped to 0..2. Rankings for older months stop being
// actionable, and an unbounded picker invites a live recompute over any month in
// the data (2023 rows exist locally), which this endpoint computes per request.
const MAX_MONTHS_BACK = 2;

const Leaderboard = () => {
    const navigate = useNavigate();

    // Corporate users land on their own organisation first, per the proposal.
    // `company` is written at sign-in, so it tells us whether the tab applies.
    const role = String(authStorage.getItem('roles') || '').toLowerCase();

    // Super-admins browse the whole membership, so they get pagination and search.
    const isAdmin = role === 'admin';

    const isEmployer = role === 'company';

    // Anyone who belongs to an organisation sees the organisation tab, employees
    // included. This was briefly employer-only; the client reversed it on
    // 2026-09-07 -- employees get BOTH tabs. The endpoint has no role gate either,
    // and scopes from the caller's own record, so a member can only read their
    // own organisation.
    const hasOrganization = Boolean(authStorage.getItem('company'));

    // What DOES still differ by role is paging: employers page their own roster
    // and admins page everyone, while members get the top 20 with no pager --
    // on both the organisation and the global tab.
    const isPaged = isAdmin || isEmployer;

    const [scope, setScope] = useState(hasOrganization ? SCOPE_ORG : SCOPE_GLOBAL);

    // Admin feature switches. Unknown reads as ENABLED, so a slow or failed
    // request shows the page as it was rather than hiding both tabs.
    const [flags, setFlags] = useState(null);
    const boardEnabled = isFeatureEnabled(FEATURE.LEADERBOARD, flags);
    const performanceEnabled = isFeatureEnabled(FEATURE.MY_PERFORMANCE, flags);
    const showBoard = boardEnabled && scope !== SCOPE_PERFORMANCE;

    /*
     * Land on a tab that actually exists. Flags arrive after first render, so the
     * initial scope above is a guess; this corrects it once they land.
     *
     * Only one correction is possible. Full Leaderboard is the master switch, so
     * `!boardEnabled` means the whole page is gone (handled by the guard below)
     * and there is no "boards off, My Performance on" state to fall into.
     */
    useEffect(() => {
        if (!flags) return;
        if (!performanceEnabled && scope === SCOPE_PERFORMANCE) {
            setScope(hasOrganization ? SCOPE_ORG : SCOPE_GLOBAL);
        }
    }, [flags, performanceEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    const [monthsBack, setMonthsBack] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    // Debounced so typing does not fire a board rebuild per keystroke -- the API
    // recomputes the goal maths for every member on each request.
    const [searchTerm, setSearchTerm] = useState('');
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const month = useMemo(
        () => moment().subtract(monthsBack, 'months').format('YYYY-MM'),
        [monthsBack]
    );
    const monthLabel = useMemo(
        () => moment().subtract(monthsBack, 'months').format('MMMM YYYY'),
        [monthsBack]
    );

    useEffect(() => {
        let cancelled = false;
        loadFeatureFlags().then((f) => { if (!cancelled) setFlags(f); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!isAdmin) return undefined;
        const t = setTimeout(() => {
            setSearchTerm(search.trim());
            setPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [search, isAdmin]);

    useEffect(() => {
        // My Performance fetches its own data, and a disabled board would 404.
        if (scope === SCOPE_PERFORMANCE || !boardEnabled) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError('');

        axios({
            method: 'GET',
            url: `${BASE_URL}/leaderboard/${scope}`,
            headers: { "x-access-token": authStorage.getItem("auth-token") },
            params: isPaged
                ? { month, page, limit: ADMIN_PAGE_SIZE, search: isAdmin ? (searchTerm || undefined) : undefined }
                : { month, page: 1, limit: TOP_N },
        }).then((res) => {
            if (cancelled) return;
            setBoard(res?.data?.data || null);
            setLoading(false);
        }).catch((err) => {
            if (cancelled) return;
            setLoading(false);
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                authStorage.clear();
                navigate('/');
                return;
            }
            setError('Could not load the leaderboard. Please try again.');
        });

        return () => { cancelled = true; };
    }, [scope, month, page, searchTerm, isAdmin, isPaged, boardEnabled, navigate]);

    const changeScope = (next) => {
        if (next === scope) return;
        setScope(next);
        setPage(1);
    };

    const changeMonth = (delta) => {
        const next = monthsBack + delta;
        // Cannot look into the future, and cannot go back past the 3-month window.
        if (next < 0 || next > MAX_MONTHS_BACK) return;
        setMonthsBack(next);
        setPage(1);
    };

    const rows = board?.rows || [];
    const me = board?.me || null;
    const myStatus = board?.myStatus;
    const myRowInTable = me && rows.some((r) => r.userId === me.userId);

    const statusMessage = () => {
        if (myStatus === 'no_device') {
            return {
                text: 'Connect a device to appear on the leaderboard. Your activity is what ranks you.',
                action: { label: 'Connect a device', to: '/device' },
            };
        }
        if (myStatus === 'opted_out') {
            return {
                text: 'You have chosen not to appear on the leaderboard. Everyone else’s board is unaffected, and you can turn this back on under My Profile.',
            };
        }
        if (myStatus === 'no_activity') {
            return {
                text: `No qualifying activity recorded in ${monthLabel} yet, so you are not ranked for this month.`,
            };
        }
        if (myStatus === 'no_organization') {
            return { text: 'Your account is not linked to an organisation, so there is no organisation leaderboard to show.' };
        }
        return null;
    };

    const notice = statusMessage();

    /**
     * The viewer's own standing, above the table.
     *
     * `me` is null unless myStatus === 'ranked' (the API omits it for a member
     * with no device or no qualifying activity), so the not-ranked states are
     * rendered from myStatus alone rather than from a partially-filled row.
     * 'no_organization' is deliberately skipped — there is no board at all in
     * that case, and the amber notice below already explains it.
     */
    const MyStanding = () => {
        if (!board || myStatus === 'no_organization') return null;

        if (!me) {
            const n = notice;
            if (!n) return null;
            return (
                <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:p-5">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-2xl font-semibold text-gray-300">
                            &ndash;
                        </div>
                        <div className="min-w-[240px] flex-1">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Your standing</p>
                            <p className="mt-1 text-sm text-gray-700">{n.text}</p>
                            {n.action && (
                                <button
                                    type="button"
                                    onClick={() => navigate(n.action.to)}
                                    className="mt-2 rounded-md bg-[#415E96] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                                >
                                    {n.action.label}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        const met = me.achievedGoal;
        const pct = Math.min(me.goalProgress, 100);
        return (
            <div className={`mb-5 rounded-xl border p-4 sm:p-5 ${met
                ? 'border-emerald-200 bg-gradient-to-r from-emerald-50 to-white'
                : 'border-[#C9D5EC] bg-gradient-to-r from-[#F3F7FF] to-white'}`}>
                <div className="flex flex-wrap items-center gap-4 sm:gap-6">

                    {/* Rank */}
                    <div className="flex shrink-0 flex-col items-center">
                        <div className={`flex h-16 w-16 items-center justify-center rounded-xl text-white ${met ? 'bg-emerald-600' : 'bg-[#415E96]'}`}>
                            <span className="text-2xl font-bold tabular-nums leading-none">{me.rank}</span>
                        </div>
                        <span className="mt-1 text-[11px] text-gray-500 tabular-nums">
                            of {board.participants}
                        </span>
                    </div>

                    {/* Name + progress */}
                    <div className="min-w-[240px] flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Your standing</p>
                            {met && (
                                <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                                    Goal met
                                </span>
                            )}
                        </div>
                        <p className="mt-0.5 text-base font-semibold text-gray-900">
                            {me.name}
                            {scope === SCOPE_GLOBAL && me.companyName && (
                                <span className="ml-2 text-xs font-normal text-gray-500">{me.companyName}</span>
                            )}
                        </p>

                        <div className="mt-2 flex items-center gap-3">
                            <span className={`text-2xl font-bold tabular-nums leading-none ${met ? 'text-emerald-700' : 'text-[#415E96]'}`}>
                                {me.goalProgress}%
                            </span>
                            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-200">
                                <div
                                    className={`h-full rounded-full ${met ? 'bg-emerald-500' : 'bg-[#415E96]'}`}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>

                        <p className="mt-1.5 text-xs text-gray-600 tabular-nums">
                            <span className="font-medium text-gray-800">{me.countedMinutes}</span> of {me.goalTarget} counted minutes
                            {met
                                ? me.minutesOverGoal > 0 && <span className="text-emerald-700"> · {me.minutesOverGoal} over</span>
                                : <span> · {me.minutesStillNeeded} to go</span>}
                        </p>
                    </div>

                    {/* Secondary stats */}
                    <div className="flex shrink-0 gap-2">
                        {[
                            { label: 'Device', value: me.device },
                            { label: 'Mod · Vig · Peak', value: `${me.moderateMinutes} · ${me.vigorousMinutes} · ${me.peakMinutes}` },
                            { label: 'Active days', value: me.activeDays },
                        ].map((s) => (
                            <div key={s.label} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-center">
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 whitespace-nowrap">{s.label}</p>
                                <p className="mt-0.5 text-sm font-semibold text-gray-800 tabular-nums whitespace-nowrap">{s.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const Row = ({ r, highlight }) => (
        <tr className={
            highlight
                ? "bg-[#EEF3FF] text-slate-900 border-0 border-solid border-b-2 border-gray-300 last:border-b-0"
                : "text-slate-900 border-0 border-solid border-b-2 border-gray-300 last:border-b-0"
        }>
            <td className="p-3 text-center font-semibold tabular-nums">{r.rank}</td>
            <td className="p-3">
                {r.name}
                {highlight && <span className="ml-2 text-xs font-medium text-[#415E96]">You</span>}
                {r.companyName && scope === SCOPE_GLOBAL && (
                    <span className="block text-xs text-gray-500">{r.companyName}</span>
                )}
            </td>
            <td className="p-3 text-center">
                <span className="inline-block rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {r.device}
                </span>
            </td>
            <td className="p-3 text-center">
                <div className="flex items-center justify-center gap-2">
                    <span className={`font-semibold tabular-nums ${r.achievedGoal ? 'text-emerald-700' : 'text-gray-800'}`}>
                        {r.goalProgress}%
                    </span>
                    {r.achievedGoal && (
                        <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Goal met
                        </span>
                    )}
                </div>
                <div className="mt-1 h-1.5 w-24 mx-auto rounded-full bg-gray-200 overflow-hidden">
                    <div
                        className={r.achievedGoal ? 'h-full bg-emerald-500' : 'h-full bg-[#415E96]'}
                        style={{ width: `${Math.min(r.goalProgress, 100)}%` }}
                    />
                </div>
            </td>
            <td className="p-3 text-center tabular-nums">
                {r.countedMinutes}
                <span className="text-gray-400"> / {r.goalTarget}</span>
            </td>
            <td className="p-3 text-center text-xs text-gray-600 tabular-nums whitespace-nowrap">
                {r.moderateMinutes} · {r.vigorousMinutes} · {r.peakMinutes}
            </td>
            <td className="p-3 text-center tabular-nums">{r.activeDays}</td>
        </tr>
    );

    /*
     * The master switch is off, so the whole section is gone. The sidebar entry is
     * hidden too, but the route still exists and somebody may have the URL
     * bookmarked, so say so plainly rather than drawing an empty tabstrip over a
     * blank card.
     */
    if (flags && !boardEnabled) {
        return (
            <div className="dark:bg-slate-900 p-6 min-h-screen">
                <h2 className="mob-header font-medium mb-5">Leaderboard</h2>
                <div className="bg-white rounded-lg p-10 text-center text-gray-500">
                    This section is not currently available.
                </div>
            </div>
        );
    }

    return (
        <div className="dark:bg-slate-900 p-6 min-h-screen">
            <h2 className="mob-header font-medium mb-5">Leaderboard</h2>

            <div className="bg-white rounded-lg p-5">

                {/* Scope + month controls */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                    {/* Real tabs, not pill buttons (client request 2026-09-17): the active
                        tab is #E53E14 with a matching underline that sits ON the container's
                        bottom rule, so the selected tab reads as joined to the panel below.

                        `border-solid` is spelled out on every border: this project loads only
                        `@tailwind utilities`, so with no preflight `border-style` defaults to
                        `none` and a bare `border-b` computes to 0px and draws nothing.

                        "My Organisation" appears only for someone who has an organisation, so
                        a member with none sees one board tab -- that is intended, not a stray
                        control to tidy away. */}
                    <div
                        role="tablist"
                        aria-label="Leaderboard scope"
                        className="flex gap-6 border-0 border-b border-solid border-gray-200"
                    >
                        {/* Each tab is gated by its own switch. With both off the
                            whole page is hidden upstream, so there is no empty-tabstrip
                            case to handle here. */}
                        {boardEnabled && hasOrganization && (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={scope === SCOPE_ORG}
                                onClick={() => changeScope(SCOPE_ORG)}
                                className="-mb-px cursor-pointer border-0 border-b-2 border-solid bg-transparent px-1 pb-3 text-sm font-medium"
                                style={{
                                    borderBottomColor: scope === SCOPE_ORG ? '#E53E14' : 'transparent',
                                    color: scope === SCOPE_ORG ? '#E53E14' : '#6b7280',
                                }}
                            >
                                My Organisation
                            </button>
                        )}
                        {boardEnabled && (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={scope === SCOPE_GLOBAL}
                                onClick={() => changeScope(SCOPE_GLOBAL)}
                                className="-mb-px cursor-pointer border-0 border-b-2 border-solid bg-transparent px-1 pb-3 text-sm font-medium"
                                style={{
                                    borderBottomColor: scope === SCOPE_GLOBAL ? '#E53E14' : 'transparent',
                                    color: scope === SCOPE_GLOBAL ? '#E53E14' : '#6b7280',
                                }}
                            >
                                Global Leaderboard
                            </button>
                        )}
                        {performanceEnabled && (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={scope === SCOPE_PERFORMANCE}
                                onClick={() => changeScope(SCOPE_PERFORMANCE)}
                                className="-mb-px cursor-pointer border-0 border-b-2 border-solid bg-transparent px-1 pb-3 text-sm font-medium"
                                style={{
                                    borderBottomColor: scope === SCOPE_PERFORMANCE ? '#E53E14' : 'transparent',
                                    color: scope === SCOPE_PERFORMANCE ? '#E53E14' : '#6b7280',
                                }}
                            >
                                My Performance
                            </button>
                        )}
                    </div>

                    {/* The search box and the month pager both steer a ranking, so they
                        are board furniture -- My Performance has its own period selector
                        and would be misread as filtering it. */}
                    {showBoard && (
                        <>
                        {isAdmin && (
                            <div className="relative order-last w-full sm:order-none sm:w-72">
                                {/* type="text", not "search": the native search input renders its
                                    own clear affordance, which collided with the Clear button. */}
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search members by name, company or email"
                                    aria-label="Search members"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 pr-16 text-sm placeholder:text-gray-400 focus:border-[#415E96] focus:outline-none"
                                />
                                {/* NOTE: this project loads only `@tailwind utilities` (see
                                    src/index.css) -- there is NO preflight, so every native control
                                    keeps its browser default styling. That is why this button has to
                                    say `border-0 appearance-none bg-transparent` explicitly: without
                                    them Chrome's default `1.79px outset` button border and button
                                    face rendered as a grey block inside the field. Vertical centring
                                    is `inset-y-0 flex items-center` rather than
                                    `top-1/2 -translate-y-1/2` so it cannot depend on transforms
                                    either. Apply the same treatment to any new bare button. */}
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch('')}
                                        className="absolute inset-y-0 right-0 flex items-center rounded-r-md border-0 bg-transparent px-3 text-xs font-medium leading-none text-gray-500 appearance-none hover:text-gray-800"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => changeMonth(1)}
                                disabled={monthsBack >= MAX_MONTHS_BACK}
                                className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Previous month"
                            >
                                &larr;
                            </button>
                            <span className="min-w-[140px] text-center text-sm font-medium text-gray-800">
                                {monthLabel}
                            </span>
                            <button
                                type="button"
                                onClick={() => changeMonth(-1)}
                                disabled={monthsBack === 0}
                                className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Next month"
                            >
                                &rarr;
                            </button>
                        </div>
                        </>
                    )}
                </div>

                {/* One panel or the other. My Performance is a wholly different
                    question -- "how have I done over time" rather than "where do I
                    stand this month" -- so none of the board chrome above or below
                    applies to it, and it fetches its own data. */}
                {scope === SCOPE_PERFORMANCE ? (
                    <MyPerformance />
                ) : (
                    <>
                    {/* The viewer's own standing, above the table */}
                    {!loading && <MyStanding />}

                    {/* Summary line */}
                    {!loading && board && (
                        <p className="mb-4 text-sm text-gray-600">
                            {scope === SCOPE_ORG && board.organization ? `${board.organization} — ` : ''}
                            <span className="font-medium text-gray-800">{board.participants}</span> ranked
                            {typeof board.achieverCount === 'number' && (
                                <>, <span className="font-medium text-emerald-700">{board.achieverCount}</span> met their goal</>
                            )}
                            {/* The no-device count is deliberately NOT shown. On the global board it
                                counts every account without a connected device — admins, the company
                                logins themselves, dormant signups — so it read as a large, alarming
                                number that has nothing to do with the ranking. `notRankedCounts.noDevice`
                                is still returned by the API if it is ever wanted somewhere narrower. */}
                            {board.notRankedCounts && board.notRankedCounts.noActivity > 0 && (
                                <span className="text-gray-500">
                                    {' '}· {board.notRankedCounts.noActivity} with no qualifying activity
                                </span>
                            )}
                        </p>
                    )}

                    {/* Only the case MyStanding skips — everything else is rendered
                        inside the card so the viewer's status appears in one place. */}
                    {!loading && notice && myStatus === 'no_organization' && (
                        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                            {notice.text}
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>
                    )}

                    {loading ? (
                        <Skeleton count={8} height={44} />
                    ) : rows.length === 0 ? (
                        <div className="py-16 text-center text-gray-500">
                            {board?.search
                                ? <>No ranked member matches &ldquo;{board.search}&rdquo; in {monthLabel}.</>
                                : <>No one is ranked for {monthLabel} yet.</>}
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left" style={{ minWidth: '760px' }}>
                                    <thead className="text-xs text-gray-700 uppercase bg-[#FFF5F3] dark:text-gray-400 rounded-xl">
                                        <tr className="text-slate-900 border-0">
                                            <th scope="col" className="p-3 text-center rounded-tl-xl">Rank</th>
                                            <th scope="col" className="p-3">Member</th>
                                            <th scope="col" className="p-3 text-center">Device</th>
                                            <th scope="col" className="p-3 text-center">Goal Progress</th>
                                            <th scope="col" className="p-3 text-center">Counted Mins</th>
                                            <th scope="col" className="p-3 text-center" title="Moderate · Vigorous · Peak minutes">Mod · Vig · Peak</th>
                                            <th scope="col" className="p-3 text-center rounded-tr-xl">Active Days</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <Row key={r.userId} r={r} highlight={me && r.userId === me.userId} />
                                        ))}
                                    </tbody>
                                    {/* The member's own row, pinned when they rank outside the top 20.
                                        With no pager this is the ONLY way to see your own position,
                                        so do not remove it. */}
                                    {me && !myRowInTable && (
                                        <tbody className="border-t-2 border-gray-300">
                                            <Row r={me} highlight />
                                        </tbody>
                                    )}
                                </table>
                            </div>

                            {/* Admins and employers page; members see the top 20 only. */}
                            {isPaged ? (
                                <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm text-gray-600">
                                    <div>
                                        Showing{' '}
                                        <span className="font-medium text-gray-800">
                                            {(board.page - 1) * ADMIN_PAGE_SIZE + 1}
                                            {'–'}
                                            {Math.min(board.page * ADMIN_PAGE_SIZE, board.total)}
                                        </span>{' '}
                                        of <span className="font-medium text-gray-800">{board.total}</span>
                                        {board.search
                                            ? <> matching &ldquo;{board.search}&rdquo; · {board.participants} ranked in total</>
                                            : ' ranked members'}
                                    </div>
                                    {board.totalPages > 1 && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setPage(board.page - 1)}
                                                disabled={board.page <= 1}
                                                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                Prev
                                            </button>
                                            <span className="px-2 tabular-nums">{board.page} / {board.totalPages}</span>
                                            <button
                                                type="button"
                                                onClick={() => setPage(board.page + 1)}
                                                disabled={board.page >= board.totalPages}
                                                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : board.total > TOP_N && (
                                <p className="pt-4 text-sm text-gray-600">
                                    Showing the top <span className="font-medium text-gray-800">{TOP_N}</span>
                                    {' '}of <span className="font-medium text-gray-800">{board.total}</span> ranked members
                                    {me && !myRowInTable && <span className="text-gray-500"> · your row is pinned below</span>}
                                </p>
                            )}

                        </>
                    )}

                    {/* The rule, stated plainly — members will ask why their target differs. */}
                    {!loading && rows.length > 0 && (
                        <p className="mt-5 border-t border-gray-200 pt-4 text-xs text-gray-500">
                            Ranked by progress against each member's own monthly goal. Fitbit-only members are
                            measured in Active Zone Minutes (Moderate &times;1, Vigorous &times;2, Peak &times;2)
                            against 600. Garmin, Apple and mixed-device members are measured in raw minutes
                            against 300 if they logged any Vigorous time, otherwise 600.
                        </p>
                    )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Leaderboard;
